'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserProfile {
  id?: string;
  applicationId?: string;
  userId?: string;
  bio?: string;
  profilePhotoUrl?: string;
  preferredContactMethod?: string;
  isActive?: boolean;
  isVerified?: boolean;
  verificationDate?: string;
  totalBookings?: number;
  completedBookings?: number;
  cancelledBookings?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const userProfileService = {
  async getProfileByUserId(userId: string): Promise<UserProfile | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        if (error.code === 'PGRST116') {
          return null;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        applicationId: data.application_id,
        userId: data.user_id,
        bio: data.bio,
        profilePhotoUrl: data.profile_photo_url,
        preferredContactMethod: data.preferred_contact_method,
        isActive: data.is_active,
        isVerified: data.is_verified,
        verificationDate: data.verification_date,
        totalBookings: data.total_bookings,
        completedBookings: data.completed_bookings,
        cancelledBookings: data.cancelled_bookings,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async updateProfile(profileId: string, updates: Partial<UserProfile>) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          bio: updates.bio,
          profile_photo_url: updates.profilePhotoUrl,
          preferred_contact_method: updates.preferredContactMethod,
        })
        .eq('id', profileId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },

  async getApplicationByProfileId(profileId: string) {
    const supabase = createClient();

    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('application_id')
        .eq('id', profileId)
        .single();

      if (profileError) throw new Error(profileError.message);

      const { data, error } = await supabase
        .from('user_applications')
        .select('*')
        .eq('id', profile.application_id)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        city: data.city,
        province: data.province,
        postalCode: data.postal_code,
        dateOfBirth: data.date_of_birth,
        alternatePhone: data.alternate_phone,
        servicePreferences: data.service_preferences,
        otherServiceDescription: data.other_service_description,
        typicalItemTypes: data.typical_item_types,
        averageItemSize: data.average_item_size,
        requiresAssemblyDisassembly: data.requires_assembly_disassembly,
        requiresPackingMaterials: data.requires_packing_materials,
        frequencyOfUse: data.frequency_of_use,
        estimatedMonthlyBookings: data.estimated_monthly_bookings,
        preferredPaymentMethod: data.preferred_payment_method,
        accessibilityRequirements: data.accessibility_requirements,
        specialHandlingNeeds: data.special_handling_needs,
        preferredTimeSlots: data.preferred_time_slots,
        emergencyContactName: data.emergency_contact_name,
        emergencyContactPhone: data.emergency_contact_phone,
        emergencyContactRelationship: data.emergency_contact_relationship,
        status: data.status,
        createdAt: data.created_at,
      };
    } catch (error: any) {
      throw error;
    }
  },
};